// Ajustes de empresa (superadmin): the single company_settings row. Working
// hours drive add_working_hours(), which sets how long an apartado lasts.
import React, { useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Card,
  Checkbox,
  InlineStack,
  Page,
  Text,
  TextField,
} from "@shopify/polaris";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";

// ISO weekday keys, as stored in working_hours.
const DAYS = [
  ["1", "Lunes"],
  ["2", "Martes"],
  ["3", "Miércoles"],
  ["4", "Jueves"],
  ["5", "Viernes"],
  ["6", "Sábado"],
  ["7", "Domingo"],
];

function CompanySettings() {
  const { claims, session } = UserAuth();
  const isSuperadmin = claims?.role === "superadmin";
  const [hours, setHours] = useState(null);
  const [holdHours, setHoldHours] = useState("8");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    supabase
      .from("company_settings")
      .select("working_hours, hold_working_hours")
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) return setMessage({ tone: "critical", text: error.message });
        setHours(data?.working_hours || {});
        setHoldHours(String(data?.hold_working_hours ?? 8));
      });
  }, []);

  const setDay = (key, value) => setHours((prev) => ({ ...prev, [key]: value }));

  const invalidDay = hours
    ? DAYS.find(([k]) => hours[k] && !(hours[k].open < hours[k].close))
    : null;
  const holdValid = Number(holdHours) > 0;

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const { error } = await supabase
      .from("company_settings")
      .update({
        working_hours: hours,
        hold_working_hours: Number(holdHours),
        updated_at: new Date().toISOString(),
        updated_by: session?.user?.id || null,
      })
      .eq("id", true);
    setSaving(false);
    setMessage(error ? { tone: "critical", text: error.message } : { tone: "success", text: "Guardado" });
  };

  if (!isSuperadmin) {
    return (
      <AppWrapper>
        <Page title="Ajustes de empresa">
          <Banner tone="warning">Solo un superadmin puede ver esta página.</Banner>
        </Page>
      </AppWrapper>
    );
  }

  return (
    <AppWrapper>
      <Page
        title="Ajustes de empresa"
        primaryAction={{
          content: "Guardar",
          onAction: save,
          loading: saving,
          disabled: !hours || !!invalidDay || !holdValid,
        }}
      >
        <BlockStack gap="400">
          {message && (
            <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
              {message.text}
            </Banner>
          )}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Horario laboral</Text>
              <Text tone="subdued">Hora de Monterrey. Los apartados solo cuentan horas dentro de este horario.</Text>
              {hours &&
                DAYS.map(([key, label]) => {
                  const day = hours[key];
                  return (
                    <InlineStack key={key} gap="300" blockAlign="center" wrap={false}>
                      <div style={{ width: 120 }}>
                        <Checkbox
                          label={label}
                          checked={!!day}
                          onChange={(on) => setDay(key, on ? { open: "09:00", close: "18:00" } : null)}
                        />
                      </div>
                      {day ? (
                        <>
                          <div style={{ width: 130 }}>
                            <TextField
                              label="Abre"
                              labelHidden
                              type="time"
                              value={day.open}
                              onChange={(v) => setDay(key, { ...day, open: v })}
                              autoComplete="off"
                            />
                          </div>
                          <Text>a</Text>
                          <div style={{ width: 130 }}>
                            <TextField
                              label="Cierra"
                              labelHidden
                              type="time"
                              value={day.close}
                              onChange={(v) => setDay(key, { ...day, close: v })}
                              autoComplete="off"
                              error={invalidDay?.[0] === key ? "Debe ser después de la apertura" : undefined}
                            />
                          </div>
                        </>
                      ) : (
                        <Text tone="subdued">Cerrado</Text>
                      )}
                    </InlineStack>
                  );
                })}
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Apartados</Text>
              <div style={{ maxWidth: 240 }}>
                <TextField
                  label="Duración (horas hábiles)"
                  type="number"
                  min={1}
                  value={holdHours}
                  onChange={setHoldHours}
                  autoComplete="off"
                  error={holdValid ? undefined : "Debe ser mayor a 0"}
                />
              </div>
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default CompanySettings;
